import React, { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import {
  CustomTextInput,
  CustomSingleSelect,
  CustomCalendarInput,
  CustomCheckbox,
  colors,
} from "@ippon/shared-components";
import type {
  ManagementTaskCustomFieldRecord,
  ManagementTaskCustomFieldSection,
} from "@/store/managementTaskCustomFieldSlice";
import type { ManagementTaskType } from "@/store/managementTaskSlice";

type Primitive = string | number | boolean | string[] | null;

export interface CustomFieldValue {
  field_id: string;
  value: Primitive;
}

interface Props {
  fields: ManagementTaskCustomFieldRecord[];
  section: ManagementTaskCustomFieldSection;
  /** 当該タスクの task_type (applies_to_task_types で絞り込み) */
  taskType?: ManagementTaskType;
  values: CustomFieldValue[];
  onChange: (next: CustomFieldValue[]) => void;
}

const CustomFieldRenderer: React.FC<Props> = ({
  fields,
  section,
  taskType,
  values,
  onChange,
}) => {
  const targetFields = useMemo(() => {
    return fields
      .filter((f) => f.section_key === section)
      .filter((f) => {
        if (!f.applies_to_task_types || f.applies_to_task_types.length === 0)
          return true;
        if (!taskType) return true;
        return f.applies_to_task_types.includes(taskType);
      })
      .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
  }, [fields, section, taskType]);

  if (targetFields.length === 0) return null;

  const valueMap = new Map(values.map((v) => [v.field_id, v.value]));

  const updateField = (fieldId: string, value: Primitive) => {
    const next = [...values.filter((v) => v.field_id !== fieldId), { field_id: fieldId, value }];
    onChange(next);
  };

  return (
    <Stack spacing={1} data-testid="management-custom-field-renderer">
      {targetFields.map((f) => (
        <FieldInput
          key={f.field_id}
          field={f}
          value={valueMap.get(f.field_id) ?? null}
          onChange={(v) => updateField(f.field_id, v)}
        />
      ))}
    </Stack>
  );
};

const FieldInput: React.FC<{
  field: ManagementTaskCustomFieldRecord;
  value: Primitive;
  onChange: (v: Primitive) => void;
}> = ({ field, value, onChange }) => {
  const { register, control, watch, setValue } = useForm<{ v: Primitive }>({
    defaultValues: { v: (value ?? "") as Primitive },
  });
  React.useEffect(() => {
    setValue("v", (value ?? "") as Primitive);
  }, [value, setValue]);
  React.useEffect(() => {
    const sub = watch((next) => {
      const v = next.v;
      if (v !== value) onChange((v ?? null) as Primitive);
    });
    return () => sub.unsubscribe();
  }, [watch, value, onChange]);

  const labelEl = (
    <Typography
      sx={{
        fontSize: 12,
        color: colors.text.primary,
        mb: 0.5,
      }}
    >
      {field.label}
    </Typography>
  );

  if (field.field_type === "text" || field.field_type === "number") {
    return (
      <Box>
        {labelEl}
        <CustomTextInput
          label=""
          name="v"
          register={register as any}
          errors={{}}
          type={field.field_type === "number" ? "number" : "text"}
          hasSpacing={false}
          inputWidth="100%"
          inputWidthSp="100%"
        />
      </Box>
    );
  }
  if (field.field_type === "textarea") {
    return (
      <Box>
        {labelEl}
        <CustomTextInput
          label=""
          name="v"
          register={register as any}
          errors={{}}
          multiline
          rows={3}
          hasSpacing={false}
          inputWidth="100%"
          inputWidthSp="100%"
        />
      </Box>
    );
  }
  if (field.field_type === "select") {
    return (
      <Box>
        {labelEl}
        <CustomSingleSelect
          label=""
          name="v"
          control={control as any}
          errors={{}}
          options={(field.options || []).map((o) => ({ label: o, value: o }))}
          inputWidth="100%"
          inputWidthSp="100%"
          hasSpacing={false}
        />
      </Box>
    );
  }
  if (field.field_type === "date") {
    return (
      <Box>
        <CustomCalendarInput
          label={field.label}
          name="v"
          control={control as any}
          inputWidth="100%"
          inputWidthSp="100%"
          marginBottom={0}
        />
      </Box>
    );
  }
  if (field.field_type === "checkbox") {
    const checked = !!value;
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CustomCheckbox
          checked={checked}
          onChange={() => onChange(!checked)}
        />
        <Typography
          sx={{
            fontSize: 13,
            color: colors.text.primary,
            cursor: "pointer",
            userSelect: "none",
          }}
          onClick={() => onChange(!checked)}
        >
          {field.label}
        </Typography>
      </Stack>
    );
  }
  return null;
};

export default CustomFieldRenderer;
